import fetch from './lib/fetch'
import { getSellingPlan } from './rechargeProductSummary'

export interface Subscription {
  id: number
  address_id: number
  customer_id: number
  analytics_data: { utm_params: string[] }
  cancellation_reason: string | null
  cancellation_reason_comments: string | null
  cancelled_at: string | null
  charge_interval_frequency: number
  created_at: string
  expire_after_specific_number_of_charges: number | null
  external_product_id: {
    ecommerce: string
  }
  external_variant_id: {
    ecommerce: string
  }
  has_queued_charges: boolean
  is_prepaid: boolean
  is_skippable: boolean
  is_swappable: boolean
  max_retries_reached: boolean
  next_charge_scheduled_at: string | null
  order_day_of_month: number | null
  order_day_of_week: number | null
  order_interval_frequency: number
  order_interval_unit: 'month' | 'week' | 'day'
  presentment_currency: string
  price: string
  product_title: string
  properties: []
  quantity: number
  sku: string
  sku_override: boolean
  status: 'active' | 'cancelled' | 'expired'
  updated_at: string
  variant_title: string | null
}

export async function getSubscriptions(
  rechargeId: string,
): Promise<Subscription[]> {
  return await fetch(
    `${
      import.meta.env.VITE_API_URL
    }/protected/subscriptions/${rechargeId}/subscriptions`,
  )
}

export async function updateSubscription(
  rechargeId: string,
  subscriptionId: number,
  update: {
    frequency: number
    quantity: number
    newVariantId?: string
  },
): Promise<{
  subscription: Subscription
}> {
  return await fetch(
    `${
      import.meta.env.VITE_API_URL
    }/protected/subscriptions/${rechargeId}/subscription/${subscriptionId}`,
    {
      method: 'PUT',
      body: JSON.stringify({
        frequency: update.frequency,
        quantity: update.quantity,
        ...(update.newVariantId ? { variant_id: update.newVariantId } : {}),
      }),
      headers: {
        'content-type': 'application/json',
      },
    },
  )
}

export async function cancelSubscription(
  rechargeId: string,
  subscriptionId: number,
  reason: string,
  comments?: string,
): Promise<{
  subscription: Subscription
}> {
  return await fetch(
    `${
      import.meta.env.VITE_API_URL
    }/protected/subscriptions/${rechargeId}/subscription/${subscriptionId}/cancel`,
    {
      method: 'POST',
      body: JSON.stringify({
        reason,
        comments,
      }),
      headers: {
        'content-type': 'application/json',
      },
    },
  )
}

export async function skipSubscription(
  rechargeId: string,
  subscriptionId: number,
): Promise<{
  subscription: Subscription
}> {
  return await fetch(
    `${
      import.meta.env.VITE_API_URL
    }/protected/subscriptions/${rechargeId}/subscription/${subscriptionId}/skip`,
    {
      method: 'POST',
    },
  )
}

export async function addSubscriptionToNextOrder(
  rechargeId: string,
  subscriptionId: number,
): Promise<{
  subscription: Subscription
}> {
  return await fetch(
    `${
      import.meta.env.VITE_API_URL
    }/protected/subscriptions/${rechargeId}/subscription/${subscriptionId}/add-to-next-order`,
    {
      method: 'POST',
    },
  )
}

export async function reactivateSubscription(
  rechargeId: string,
  subscriptionId: number,
  scheduledAt: string,
  quantity: number,
  frequency: number,
): Promise<Subscription> {
  return await fetch(
    `${
      import.meta.env.VITE_API_URL
    }/protected/subscriptions/${rechargeId}/subscription/${subscriptionId}/restart`,
    {
      method: 'POST',
      body: JSON.stringify({
        scheduled_at: scheduledAt,
        quantity,
        frequency,
      }),
      headers: {
        'content-type': 'application/json',
      },
    },
  )
}

export async function swapSubscription(
  rechargeId: string,
  subscriptionId: number,
  variantId: string,
): Promise<{
  subscription: Subscription
}> {
  return await fetch(
    `${
      import.meta.env.VITE_API_URL
    }/protected/subscriptions/${rechargeId}/subscription/${subscriptionId}/swap`,
    {
      method: 'PUT',
      body: JSON.stringify({
        external_variant_id: variantId,
      }),
      headers: {
        'content-type': 'application/json',
      },
    },
  )
}

export async function delaySubscription(
  rechargeId: string,
  subscriptionId: number,
  until: string,
): Promise<{
  subscription: Subscription
}> {
  return await fetch(
    `${
      import.meta.env.VITE_API_URL
    }/protected/subscriptions/${rechargeId}/subscription/${subscriptionId}/delay`,
    {
      method: 'PUT',
      body: JSON.stringify({
        until,
      }),
      headers: {
        'content-type': 'application/json',
      },
    },
  )
}

export const updateChargeDate = delaySubscription

export async function addNewSubscriptionToNextOrder(
  rechargeId: string,
  productId: string,
  variantId: string,
  quantity: number,
  planId: string,
  offerPercent?: number,
): Promise<{
  subscription: Subscription
}> {
  const rechargeSellingPlan = await getSellingPlan(productId, planId)
  if (!rechargeSellingPlan) {
    throw new Error(`Selling plan ${planId} not found for product ${productId}`)
  }
  return await fetch(
    `${
      import.meta.env.VITE_API_URL
    }/protected/subscriptions/${rechargeId}/add-to-next-order`,
    {
      method: 'POST',
      body: JSON.stringify({
        external_product_id: productId,
        external_variant_id: variantId,
        quantity,
        plan_id: rechargeSellingPlan.id,
        ...(offerPercent ? { offer_percent: offerPercent } : {}),
      }),
      headers: {
        'content-type': 'application/json',
      },
    },
  )
}
